<template>
  <div
    v-if="commitment"
    class="flex flex-col gap-2"
  >
    <div class="flex h-2 gap-0.5 overflow-hidden rounded-full bg-secondary">
      <div
        v-for="(investor, index) in investors"
        :key="index"
        class="h-full first:rounded-l-full last:rounded-r-full"
        :style="{ width: `${Math.max(shareFraction(investor) * 100, 0)}%`, background: investorBarColor(index) }"
      />
    </div>
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground">
        <span
          v-for="(investor, index) in investors"
          :key="index"
          class="inline-flex items-center gap-1.5"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :style="{ background: investorBarColor(index) }"
          />
          {{ investor.accountName || `Investor ${index + 1}` }} · {{ percentLabel(investor) }}
        </span>
      </div>
      <span
        class="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold"
        :class="isFullyAllocated ? 'text-success' : 'text-warning'"
      >
        <Check
          v-if="isFullyAllocated"
          class="size-3.5"
        />
        <CircleAlert
          v-else
          class="size-3.5"
        />
        {{ isFullyAllocated ? 'Fully allocated' : `${money(remaining)} left to allocate` }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Check, CircleAlert } from '@lucide/vue'

import { getCurrencySymbol } from '@/helpers/common'
import { numberFormat } from '@/helpers/numbers'
import { FRACTION_DIGITS } from '@/const'

import { investorBarColor, investorPercent } from '../utils/helpers'
import { WizardInvestor } from '../utils/types'

/*
 * The split, drawn once and read by both its editor and its read-only
 * summary: the investing-entities step shows this above the rows it lets you
 * change, and the wizard's own summary shows the same bar above the rows it
 * only lets you review — see InvestorsStep.vue and WizardSummary.vue.
 *
 * Self-guarding on `commitment` rather than leaving that to each caller: a
 * split has nothing to show a fraction of until the amount being split is
 * known, and both callers would otherwise have to repeat the same `v-if`.
 */
const { investors, commitment, currency } = defineProps<{
  investors: WizardInvestor[]
  /** The whole amount being split. Undefined until the commitment itself is filled in. */
  commitment?: number
  currency?: string
}>()

const shareFraction = (investor: WizardInvestor) =>
  commitment ? (investor.share ?? 0) / commitment : 0

const percentLabel = (investor: WizardInvestor) => {
  const percent = investorPercent(investor, commitment)
  return percent === undefined ? '—' : `${percent}%`
}

const allocated = computed(() =>
  investors.reduce((sum, investor) => sum + (investor.share ?? 0), 0))

const remaining = computed(() => (commitment ?? 0) - allocated.value)

const isFullyAllocated = computed(() => !!commitment && Math.abs(remaining.value) < 0.01)

const money = (amount?: number) =>
  amount === undefined
    ? '—'
    : numberFormat(amount, {
      currency: getCurrencySymbol(currency),
      fractionDigits: 0,
      fractionDigitsMax: FRACTION_DIGITS,
    })
</script>
